import { useAppSelector } from './state/hooks'
import { useMemo } from 'react'
import { GraphQLTaggedNode, useFragment, useLazyLoadQuery } from 'react-relay'
import {
  selectAuth,
  selectUserKey,
  selectViewingUserId,
} from './state/appSlice'
import { graphql } from 'relay-runtime'
import {
  hooksUseUserRoleCheck$key,
  Role,
} from './__generated__/hooksUseUserRoleCheck.graphql'
import { AppQuery$data } from './__generated__/AppQuery.graphql'
import { hooksFeatureFlagsQuery } from './__generated__/hooksFeatureFlagsQuery.graphql'

interface MinimumUseUserBrandsType {
  readonly accessibleBrands: readonly {
    readonly id: string
    readonly updatedAt: string
  }[]
}

type AccessibleBrand<T extends MinimumUseUserBrandsType> =
  T['accessibleBrands'][number]

export const useUserBrands = <T extends MinimumUseUserBrandsType>(
  fragment: GraphQLTaggedNode
) => {
  const userKey = useAppSelector(selectUserKey)
  const user = useFragment(fragment, userKey as AppQuery$data['getUser']) as T
  return useMemo(() => {
    const brands = (user?.accessibleBrands ??
      []) as readonly AccessibleBrand<T>[]
    const uniqueBrands = [...new Map(brands.map((v) => [v.id, v])).values()]
    return uniqueBrands.sort(
      (a, b) =>
        new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
    )
  }, [user])
}

interface UseRoleCheckProps {
  roles: Role[]
  brandId?: string
  mediaPlanId?: string
  mediaPlanIds?: string[]
}

const useUserRoleCheckFragment = graphql`
  fragment hooksUseUserRoleCheck on User {
    superuser
    accountRoles {
      role
    }
    userBrands {
      roles {
        role
      }
      brand {
        id
      }
    }
  }
`

export const useUserRoleCheck = ({ roles, brandId }: UseRoleCheckProps) => {
  const userKey = useAppSelector(selectUserKey)
  const user = useFragment(
    useUserRoleCheckFragment,
    userKey as hooksUseUserRoleCheck$key
  )
  return useMemo(() => {
    let existingRoles: Role[] = []
    if (user != null) {
      if (user.superuser) {
        return true
      }
      if (user.accountRoles.length > 0) {
        existingRoles.push(...user.accountRoles.map((it) => it.role))
      }
      if (brandId != null) {
        existingRoles.push(
          ...user.userBrands
            .filter((it) => it.brand.id === brandId)
            .flatMap((it) => it.roles)
            .map((it) => it.role)
        )
      }
    }
    return existingRoles.filter((it) => roles.includes(it)).length > 0
  }, [user, roles, brandId])
}


const QUERY_FEATURE_FLAGS = graphql`
  query hooksFeatureFlagsQuery($input: GetRequest!, $skip: Boolean!) {
    getUser(input: $input) @skip(if: $skip) {
      id
      featureFlags
    }
  }
`

export const useFeatureFlag = (
  slug: string,
  featureFlags?: readonly string[] | null
): boolean => {
  const viewingUserId = useAppSelector(selectViewingUserId)
  const data = useLazyLoadQuery<hooksFeatureFlagsQuery>(QUERY_FEATURE_FLAGS, {
    skip: !!featureFlags?.length,
    input: {
      id: viewingUserId!,
    },
  })

  return useMemo(() => {
    const list = featureFlags || data.getUser?.featureFlags || []
    return !!list?.find((ff) => ff === slug)
  }, [data.getUser?.featureFlags, featureFlags, slug])
}
